import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid2,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  FormControlLabel,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Zoom,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Slider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PhoneIcon from '@mui/icons-material/Phone';
import VideocamIcon from '@mui/icons-material/Videocam';
import StorageIcon from '@mui/icons-material/Storage';
import DevicesIcon from '@mui/icons-material/Devices';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { httpService } from '../../services/httpService';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getCurrencySymbol } from '../../utils/currency';

interface PriceTier {
  user_tier: string;
  min_users: number;
  max_users?: number;
  monthly: number;
  yearly: number;
}

interface Plan {
  _id: {
    $oid: string;
  };
  plan_id: string;
  plan_name: string;
  description: string;
  currency: string;
  price: PriceTier[];
  features: {
    chat: {
      enabled: boolean;
      message_limit: number;
      file_sharing: boolean;
      max_file_size_mb: number;
      user_presence_indicator: boolean;
      typing_indicators: boolean;
      reaction_on_messages: boolean;
      public_groups: boolean;
      delete_single_message: boolean;
      delete_conversation: boolean;
      read_and_delivery_receipts: boolean;
      mentions: boolean;
      threaded_messages: boolean;
      quote_replies: boolean;
      private_and_protected_groups: boolean;
      search_in_messages: boolean;
      block_users: boolean;
      ban_user: boolean;
      mute_users: boolean;
      voice_audio_messages: boolean;
      pin_messages: boolean;
      polls_in_groups: boolean;
    };
    audio_call: {
      enabled: boolean;
      monthly_minutes: number;
      max_participants: number;
      recording_enabled: boolean;
      one_to_one_call: boolean;
      group_call: boolean;
      invite_users: boolean;
      share_screen: boolean;
      remove_participants: boolean;
      microphone_control: boolean;
    };
    video_call: {
      enabled: boolean;
      monthly_minutes: number;
      max_participants: number;
      resolution_limit: string;
      recording_enabled: boolean;
      one_to_one_call: boolean;
      group_call: boolean;
      invite_users: boolean;
      share_screen: boolean;
      remove_participants: boolean;
      microphone_control: boolean;
    };
  };
  limits: {
    storage_gb: number;
    max_devices: number;
  };
  overage_charges: {
    audio_per_minute: number;
    video_per_minute: number;
    extra_storage_per_gb: number;
    per_extra_user: number;
  };
  validity: {
    type: string;
    duration_days: number;
  };
  status: string;
  created_at: {
    $date: string;
  };
  updated_at: {
    $date: string;
  };
  __v: number;
  is_contact_to_sales?: boolean;
  is_popular?: boolean;
  buttonText?: string;
  buttonVariant?: 'outlined' | 'contained';
}

interface PlansProps {
  onPlanSelect: (plan: Plan, tierIndex: number, yearly: boolean, currency: 'USD', convertedPlan: Plan) => void;
  selectedTierIndex: number;
  isYearly: boolean;
}

const Plans: React.FC<PlansProps> = ({ onPlanSelect, selectedTierIndex: initialTierIndex, isYearly: initialIsYearly }) => {
  const navigate = useNavigate();
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const activePlanDetails = userDetails?.activePlanDetails;
  const planInfo = userDetails?.plan_details;
  const activePlanId = activePlanDetails?.plan_id || planInfo?.plan_id;
  
  // Fixed to USD only
  const FIXED_CURRENCY = 'USD';
  
  const [isYearly, setIsYearly] = useState(initialIsYearly);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const [selectedTierIndex, setSelectedTierIndex] = useState<number>(initialTierIndex);
  const availableTiers = plans.length > 0 && plans[0].price.length > 0 ? plans[0].price : [];

  // Helper function to check if a plan is currently active
  const isActivePlan = (planId: string): boolean => {
    return activePlanId === planId;
  };

  // Get plan price in USD (no conversion needed)
  const getPlanPrice = (plan: Plan): PriceTier => {
    const baseTier = plan.price[selectedTierIndex] || plan.price[0];
    return baseTier;
  };

  // Fetch plans from API
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await httpService.get<any>(
          '/user/getAllActiveOrgPlans'
        );
        // Handle different possible response structures
        let plansData: Plan[] = [];
        
        if (response.data.status === 200 && response.data.data) {
          // Check if data.data is an object with a plans property
          if (response.data.data.plans && Array.isArray(response.data.data.plans)) {
            plansData = response.data.data.plans;
          } 
          // Check if data.data is already an array
          else if (Array.isArray(response.data.data)) {
            plansData = response.data.data;
          }
          // Check if data.data is an object and convert its values to array
          else if (typeof response.data.data === 'object') {
            plansData = Object.values(response.data.data);
          }
        } else if (response.data.data) {
          plansData = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
        } else if (Array.isArray(response.data)) {
          plansData = response.data;
        } else {
          console.error('Unexpected API response structure:', response.data);
          setError('Unexpected response format from server');
          return;
        }
        
        setPlans(plansData);
        
      } catch (err: any) {
        console.error('Error fetching plans:', err);
        console.error('Error response:', err.response);
        setError(err.response?.data?.message || err.message || 'Failed to load plans. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const handlePlanSelect = (planId: string) => {
    const selectedPlan = plans.find(plan => plan.plan_id === planId);
    if (!selectedPlan) return;

    const currentPrice = getPlanPrice(selectedPlan);

    // Handle free plan
    if (currentPrice.monthly === 0) {
      console.log('Free plan selected');
      // Redirect to dashboard or handle free plan activation
      return;
    }

    // Handle contact to sales plan
    if (selectedPlan.is_contact_to_sales) {
      navigate('/support');
      return;
    }

    // Create a plan with USD currency (no conversion needed)
    const convertedPlan = {
      ...selectedPlan,
      currency: FIXED_CURRENCY,
      price: selectedPlan.price // Keep original USD prices
    };

    // Store plan selection and switch to billing tab (always USD)
    onPlanSelect(selectedPlan, selectedTierIndex, isYearly, FIXED_CURRENCY, convertedPlan);
  };

  // Listen for payment events - removed since payment is now handled in Billing component

  return (
    <Box sx={{ width: '100%', mx: 'auto' }}>
      <Box textAlign="center" mb={6}>
        <Fade in={true} timeout={800}>
          <Box>
            <Typography 
              variant="h5" 
              component="h1" 
              gutterBottom 
              fontWeight="bold"
              sx={{
                background: 'linear-gradient(135deg, #007aff 0%, #5fb0ff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Choose Your Perfect Plan
            </Typography>
            <Typography color="text.secondary" mb={4} sx={{ maxWidth: 600, mx: 'auto' }}>
              Unlock powerful features for your communication needs. Switch between monthly and yearly billing.
            </Typography>
          </Box>
        </Fade>

        <Box display="flex" gap={2} flexWrap="wrap" justifyContent="center">
          <Paper 
            elevation={3} 
            sx={{ 
              display: 'inline-flex', 
              p: 1.5, 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #e5f3ff 0%, #cce7ff 100%)',
              border: '2px solid rgba(0, 122, 255, 0.2)'
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={isYearly}
                  onChange={(e) => setIsYearly(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#007aff',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#007aff',
                    },
                  }}
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Typography fontWeight={!isYearly ? 'bold' : 'normal'} color={!isYearly ? 'primary' : 'text.secondary'}>
                    Monthly
                  </Typography>
                  <Typography fontWeight={isYearly ? 'bold' : 'normal'} color={isYearly ? 'primary' : 'text.secondary'}>
                    Yearly
                  </Typography>
                </Box>
              }
            />
          </Paper>

          <Paper 
            elevation={3} 
            sx={{ 
              display: 'inline-flex', 
              alignItems: 'center',
              px: 3,
              py: 1.5, 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #e5f3ff 0%, #cce7ff 100%)',
              border: '2px solid rgba(0, 122, 255, 0.2)'
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Typography fontWeight="bold" color="#007aff" fontSize="1.1rem">
                💵 All prices in USD
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>

      {!loading && !error && plans.length > 0 && availableTiers.length > 0 && (
        <Fade in={true} timeout={600}>
          <Box mb={6}>
            <Paper
              elevation={4}
              sx={{
                p: 2,
                borderRadius: 4,
                background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
                border: '2px solid rgba(0, 122, 255, 0.2)',
                boxShadow: '0 8px 32px rgba(0, 122, 255, 0.1)',
              }}
            >
              <Box textAlign="center" mb={1}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ 
                  color: '#007aff',
                }}>
                  Select Number of Users
                </Typography>
              </Box>

              {/* Tier Slider */}
              <Box sx={{ px: { xs: 4, sm: 6, md: 10 }, pb: 4, pt: 1, position: 'relative' }}>
                <Slider
                  value={selectedTierIndex}
                  onChange={(_, value) => setSelectedTierIndex(value as number)}
                  min={0}
                  max={availableTiers.length - 1}
                  step={1}
                  marks={availableTiers.map((tier, index) => ({
                    value: index,
                    label: tier.user_tier,
                  }))}
                  sx={{
                    width: '100%',
                    height: 8,
                    '& .MuiSlider-track': {
                      background: 'linear-gradient(90deg, #007aff 0%, #5fb0ff 100%)',
                      border: 'none',
                      height: 8,
                      borderRadius: 4,
                      boxShadow: '0 2px 8px rgba(0, 122, 255, 0.3)',
                    },
                    '& .MuiSlider-rail': {
                      background: 'rgba(0, 122, 255, 0.15)',
                      opacity: 1,
                      height: 8,
                      borderRadius: 4,
                    },
                    '& .MuiSlider-thumb': {
                      width: 35,
                      height: 35,
                      background: '#ffffff',
                      border: '5px solid #007aff',
                      boxShadow: '0 4px 12px rgba(0, 122, 255, 0.5)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:before': {
                        boxShadow: 'none',
                      },
                      '&:hover': {
                        boxShadow: '0 6px 16px rgba(0, 122, 255, 0.6)',
                      },
                    },
                    '& .MuiSlider-mark': {
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: '#ffffff',
                      border: '3px solid #007aff',
                      opacity: 1,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      cursor: 'pointer',
                      '&:hover': {
                        width: 18,
                        height: 18,
                        border: '4px solid #007aff',
                      },
                      '&.MuiSlider-markActive': {
                        backgroundColor: '#007aff',
                        border: '3px solid #ffffff',
                        boxShadow: '0 0 0 2px #007aff',
                      },
                    },
                    '& .MuiSlider-markLabel': {
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      color: '#007aff',
                      top: 45,
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      '&.MuiSlider-markLabelActive': {
                        color: '#ec4899',
                        fontWeight: 800,
                        fontSize: '1rem',
                      },
                      '&[data-index="0"]': {
                        left: '0 !important',
                        transform: 'none',
                      },
                    },
                  }}
                />
              </Box>
            </Paper>
          </Box>
        </Fade>
      )}

      {/* Loading State */}
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Plans Grid */}
      {!loading && !error && plans.length === 0 && (
        <Alert severity="info" sx={{ mb: 4 }}>
          No active plans available at the moment.
        </Alert>
      )}

      {!loading && !error && plans.length > 0 && (
        <Grid2 container spacing={2} justifyContent="center" alignItems="flex-start">
        {plans.map((plan, index) => (
          <Grid2 
            size={{ 
              xs: 12, 
              sm: plans.length === 1 ? 12 : 6, 
              md: plans.length === 1 ? 6 : plans.length === 2 ? 6 : plans.length === 3 ? 4 : 3 
            }}
            key={plan.plan_id} 
            sx={{ display: 'flex' }}
          >
            <Zoom in={true} style={{ transitionDelay: `${index * 100}ms`, width: '100%' }}>
              <Card
                elevation={0}
                sx={{
                  position: 'relative',
                  width: '100%',
                  background: plan.is_popular 
                    ? 'linear-gradient(135deg, #007aff 0%, #5fb0ff 100%)'
                    : 'linear-gradient(135deg, #f5f7fa 0%, #e5f3ff 100%)',
                  border: plan.is_popular ? 'none' : '1px solid rgba(0,0,0,0.08)',
                  borderRadius: 4,
                  overflow: 'visible',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-12px) scale(1.02)',
                    boxShadow: plan.is_popular 
                      ? '0 20px 40px rgba(0, 122, 255, 0.4)'
                      : '0 20px 40px rgba(0,0,0,0.15)',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: 4,
                    padding: plan.is_popular ? '3px' : '0',
                    background: plan.is_popular 
                      ? 'linear-gradient(135deg, #007aff, #5fb0ff, #80c9ff)'
                      : 'transparent',
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                  }
                }}
              >
                {plan.is_popular && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -18,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'linear-gradient(135deg, #007aff 0%, #0051d5 100%)',
                      color: 'white',
                      px: 3,
                      py: 1,
                      borderRadius: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      boxShadow: '0 4px 15px rgba(0, 122, 255, 0.5)',
                      zIndex: 3,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <StarIcon fontSize="small" />
                    <Typography variant="body2" fontWeight="bold">
                      Most Popular
                    </Typography>
                  </Box>
                )}

                {/* Current Plan Badge */}
                {isActivePlan(plan.plan_id) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      overflow: 'hidden',
                      width: 120,
                      height: 120,
                      zIndex: 2,
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 20,
                        left: -30,
                        width: 150,
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                        color: 'white',
                        py: 0.75,
                        transform: 'rotate(-45deg)',
                        boxShadow: '0 4px 8px rgba(76, 175, 80, 0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                        letterSpacing: '0.5px',
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: 14 }} />
                      <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem', textTransform: 'uppercase' }}>
                        Active
                      </Typography>
                    </Box>
                  </Box>
                )}

                <CardContent 
                  sx={{ 
                    p: { xs: 2.5, sm: 3, md: 3 }, 
                    textAlign: 'center',
                    background: 'white',
                    borderRadius: 4,
                  }}
                >
                  {/* Plan Name with Icon */}
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        background: plan.is_popular 
                          ? 'linear-gradient(135deg, #007aff 0%, #0051d5 100%)'
                          : 'linear-gradient(135deg, #5fb0ff 0%, #3399ff 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 12px',
                        boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                        transition: 'transform 0.3s ease',
                        '&:hover': {
                          transform: 'rotate(360deg) scale(1.1)',
                        }
                      }}
                    >
                      <TrendingUpIcon sx={{ color: 'white', fontSize: 30 }} />
                    </Box>
                    <Typography 
                      variant="h5" 
                      component="h2" 
                      fontWeight="bold" 
                      sx={{
                        background: plan.is_popular 
                          ? 'linear-gradient(135deg, #007aff 0%, #0051d5 100%)'
                          : 'linear-gradient(135deg, #5fb0ff 0%, #007aff 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1
                      }}
                    >
                      {plan.plan_name}
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary" mb={2} sx={{ minHeight: 40, height: '130px', fontSize: '0.875rem' }}>
                    {plan.description}
                  </Typography>

                  {/* Price Section */}
                  <Box 
                    mb={2}
                    sx={{
                      background: plan.is_popular 
                        ? 'linear-gradient(135deg, rgba(0, 122, 255, 0.1) 0%, rgba(95, 176, 255, 0.1) 100%)'
                        : 'linear-gradient(135deg, rgba(95, 176, 255, 0.1) 0%, rgba(0, 122, 255, 0.05) 100%)',
                      borderRadius: 2,
                      p: 2,
                      border: plan.is_popular ? '2px solid rgba(0, 122, 255, 0.3)' : '2px solid rgba(95, 176, 255, 0.3)',
                    }}
                  >
                    <Typography 
                      variant="h5" 
                      component="div" 
                      fontWeight="bold"
                      sx={{
                        background: plan.is_popular 
                          ? 'linear-gradient(135deg, #007aff 0%, #0051d5 100%)'
                          : 'linear-gradient(135deg, #5fb0ff 0%, #007aff 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      {getCurrencySymbol(FIXED_CURRENCY)}
                      {(isYearly ? getPlanPrice(plan).yearly : getPlanPrice(plan).monthly).toLocaleString('en-US')}
                      <Typography variant="h6" component="span" color="text.secondary">
                        /{isYearly ? 'year' : 'month'}
                      </Typography>
                    </Typography>
                  </Box>

                  {/* CTA Button */}
                  <Button
                    variant={plan.is_popular ? 'contained' : 'outlined'}
                    size="medium"
                    fullWidth
                    sx={{ 
                      mb: 1.5, 
                      py: 1.2,
                      borderRadius: 2,
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      textTransform: 'none',
                      background: plan.is_popular 
                        ? 'linear-gradient(135deg, #007aff 0%, #0051d5 100%)'
                        : 'transparent',
                      border: plan.is_popular ? 'none' : '2px solid #007aff',
                      color: plan.is_popular ? 'white' : '#007aff',
                      boxShadow: plan.is_popular ? '0 4px 15px rgba(0, 122, 255, 0.4)' : 'none',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: plan.is_popular 
                          ? 'linear-gradient(135deg, #0051d5 0%, #007aff 100%)'
                          : 'linear-gradient(135deg, #007aff 0%, #5fb0ff 100%)',
                        color: 'white',
                        transform: 'scale(1.05)',
                        boxShadow: '0 6px 20px rgba(0, 122, 255, 0.4)',
                      },
                      '&:disabled': {
                        background: '#ccc',
                        color: '#666'
                      }
                    }}
                    onClick={() => handlePlanSelect(plan.plan_id)}
                    disabled={(isYearly ? getPlanPrice(plan).yearly : getPlanPrice(plan).monthly) === 0}
                  >
                    {plan.is_contact_to_sales ? 'Contact Sales' : 'Select Plan'}
                  </Button>

                  {/* Features Section */}
                  <Box sx={{ mt: 2 }}>
                    <Divider sx={{ mb: 3 }}>
                      <Chip 
                        label="Features" 
                        size="small"
                        sx={{
                          background: plan.is_popular 
                            ? 'linear-gradient(135deg, #007aff 0%, #0051d5 100%)'
                            : 'linear-gradient(135deg, #5fb0ff 0%, #007aff 100%)',
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    </Divider>

                    {/* Features List */}
                    <List dense sx={{ textAlign: 'left' }}>
                    {/* Chat Features */}
                    {plan.features.chat.enabled && (
                      <>
                          <ListItem 
                            sx={{ 
                              px: 0, 
                              py: 1,
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 40, transition: 'color 0.2s ease' }}>
                              <ChatBubbleOutlineIcon color="primary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={`Unlimited messages`}
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                            />
                          </ListItem>
                        {plan.features.chat.file_sharing && (
                            <ListItem 
                              sx={{ 
                                px: 0, 
                                py: 1,
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 40, transition: 'color 0.2s ease' }}>
                                <CheckCircleIcon color="success" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary={`File sharing up to ${plan.features.chat.max_file_size_mb}MB`}
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                              />
                            </ListItem>
                        )}
                      </>
                    )}
                    
                    {/* Audio Call Features */}
                    {plan.features.audio_call.enabled && (
                      <>
                          <ListItem 
                            sx={{ 
                              px: 0, 
                              py: 1,
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 40, transition: 'color 0.2s ease' }}>
                              <CheckCircleIcon color="success" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={`Audio Call Charges/min ${getCurrencySymbol(FIXED_CURRENCY)}${plan.overage_charges.audio_per_minute}`}
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                            />
                          </ListItem>
                        {plan.features.audio_call.recording_enabled && (
                            <ListItem 
                              sx={{ 
                                px: 0, 
                                py: 1,
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 40, transition: 'color 0.2s ease' }}>
                                <CheckCircleIcon color="success" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary="Audio recording enabled"
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                              />
                            </ListItem>
                        )}
                      </>
                    )}
                    
                    {/* Video Call Features */}
                    {plan.features.video_call.enabled && (
                      <>
                          <ListItem 
                            sx={{ 
                              px: 0, 
                              py: 1,
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 40, transition: 'color 0.2s ease' }}>
                              <CheckCircleIcon color="success" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={`Video Call Charges/min ${getCurrencySymbol(FIXED_CURRENCY)}${plan.overage_charges.video_per_minute}`}
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                            />
                          </ListItem>
                          <ListItem 
                            sx={{ 
                              px: 0, 
                              py: 1,
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 40, transition: 'color 0.2s ease' }}>
                              <CheckCircleIcon color="success" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={`Video quality: ${plan.features.video_call.resolution_limit}`}
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                            />
                          </ListItem>
                        {plan.features.video_call.recording_enabled && (
                            <ListItem 
                              sx={{ 
                                px: 0, 
                                py: 1,
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 40, transition: 'color 0.2s ease' }}>
                                <CheckCircleIcon color="success" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary="Video recording enabled"
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                              />
                            </ListItem>
                        )}
                      </>
                    )}
                    
                    {/* Storage Limits */}
                      <ListItem 
                        sx={{ 
                          px: 0, 
                          py: 1,
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40, transition: 'color 0.2s ease' }}>
                          <StorageIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${plan.limits.storage_gb}GB storage`}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        />
                      </ListItem>
                    {/* </Tooltip> */}
                    
                      <ListItem 
                        sx={{ 
                          px: 0, 
                          py: 1,
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40, transition: 'color 0.2s ease' }}>
                          <DevicesIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`Up to ${plan.limits.max_devices} devices`}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        />
                      </ListItem>
                  </List>
                  </Box>
                </CardContent>
              </Card>
            </Zoom>
          </Grid2>
        ))}
        </Grid2>
      )}

      {/* Compare Plans Button */}
      {!loading && !error && plans.length > 1 && (
        <Fade in={true} timeout={1000}>
          <Box textAlign="center" mt={6}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<CompareArrowsIcon />}
              onClick={() => setCompareModalOpen(true)}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 3,
                fontWeight: 'bold',
                fontSize: '1rem',
                border: '2px solid #007aff',
                color: '#007aff',
                background: 'linear-gradient(135deg, rgba(0, 122, 255, 0.05) 0%, rgba(95, 176, 255, 0.05) 100%)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #007aff 0%, #5fb0ff 100%)',
                  color: 'white',
                  border: '2px solid #007aff',
                  transform: 'scale(1.05)',
                  boxShadow: '0 8px 20px rgba(0, 122, 255, 0.4)',
                },
              }}
            >
              Compare All Plans
            </Button>
          </Box>
        </Fade>
      )}

      {/* Feature Comparison Modal */}
      <Dialog
        open={compareModalOpen}
        onClose={() => setCompareModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #007aff 0%, #5fb0ff 100%)',
            color: 'white',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 2.5,
            position: 'relative',
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <CompareArrowsIcon fontSize="large" />
            <Typography variant="h5" fontWeight="bold">
              Compare Plan Features
            </Typography>
          </Box>
          <IconButton
            onClick={() => setCompareModalOpen(false)}
            sx={{
              color: 'white',
              position: 'absolute',
              right: 16,
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <TableContainer sx={{ maxHeight: 'calc(90vh - 180px)' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      background: 'linear-gradient(135deg, #e5f3ff 0%, #cce7ff 100%)',
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      minWidth: 200,
                      position: 'sticky',
                      left: 0,
                      top: 0,
                      zIndex: 4,
                      borderRight: '2px solid rgba(0, 122, 255, 0.2)',
                      py: 1.5,
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <CompareArrowsIcon fontSize="small" color="primary" />
                      <Typography variant="body1" fontWeight="bold" color="primary">
                        Features
                      </Typography>
                    </Box>
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell
                      key={plan.plan_id}
                      align="center"
                      sx={{
                        background: plan.is_popular
                          ? 'linear-gradient(135deg, #007aff 0%, #5fb0ff 100%)'
                          : 'linear-gradient(135deg, #e5f3ff 0%, #cce7ff 100%)',
                        color: plan.is_popular ? 'white' : '#007aff',
                        fontWeight: 'bold',
                        fontSize: '0.875rem',
                        minWidth: 180,
                        position: 'sticky',
                        top: 0,
                        zIndex: 3,
                        py: 1.5,
                      }}
                    >
                      <Box>
                        <Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5 }}>
                          {plan.plan_name}
                        </Typography>
                        {plan.is_popular && (
                          <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                            <StarIcon fontSize="small" />
                            <Typography variant="caption" fontWeight="bold">
                              Popular
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {/* Price Row */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                    }}
                  >
                    💰 Price
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      <Box>
                        {plan.price.map((tier, idx) => (
                          <Box key={idx} mb={1}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {tier.user_tier}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="primary">
                              {getCurrencySymbol(FIXED_CURRENCY)}
                              {(isYearly ? tier.yearly : tier.monthly).toLocaleString('en-US')}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>

                {/* Chat Messages */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <ChatBubbleOutlineIcon fontSize="small" color="primary" />
                      Chat Messages
                    </Box>
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.chat.enabled ? (
                        <Typography variant="body2" fontWeight="500">
                          Unlimited
                        </Typography>
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* File Sharing */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                    }}
                  >
                    📎 File Sharing
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.chat.file_sharing ? (
                        <Box>
                          <CheckIcon color="success" />
                          <Typography variant="caption" display="block">
                            up to {plan.features.chat.max_file_size_mb}MB
                          </Typography>
                        </Box>
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Audio Recording */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                    }}
                  >
                    🎙️ Audio Recording
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.audio_call.recording_enabled ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>                

                {/* Video Quality */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                    }}
                  >
                    🎬 Video Quality
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.video_call.enabled ? (
                        <Chip
                          label={plan.features.video_call.resolution_limit}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Video Recording */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                    }}
                  >
                    📹 Video Recording
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.video_call.recording_enabled ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Additional Chat Features Header */}
                <TableRow>
                  <TableCell
                    colSpan={plans.length + 1}
                    sx={{
                      background: 'linear-gradient(135deg, rgba(0, 122, 255, 0.1) 0%, rgba(95, 176, 255, 0.1) 100%)',
                      fontWeight: 'bold',
                      fontSize: '0.95rem',
                      py: 1.5,
                      textAlign: 'center',
                    }}
                  >
                    💬 Additional Chat Features
                  </TableCell>
                </TableRow>

                {/* User Presence Indicator */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    👤 User Presence Indicator
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.chat.user_presence_indicator ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Typing Indicator */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    ⌨️ Typing Indicator
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.chat.typing_indicators ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Reaction on Messages */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    👍 Reaction on Messages
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.chat.reaction_on_messages ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Public Groups */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    👥 Public Groups
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.chat.public_groups ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Delete Single Message */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    🗑️ Delete Single Message
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.chat.delete_single_message ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Delete Conversation */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    🗑️ Delete Conversation
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.chat.delete_conversation ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Read and Delivery Receipts */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    📨 Read and Delivery Receipts
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.chat.read_and_delivery_receipts ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Mentions */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    @ Mentions
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.chat.mentions ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Threaded Messages */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    🧵 Threaded Messages
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.chat.threaded_messages ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Quote Replies */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    💬 Quote Replies
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.chat.quote_replies ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Private and Protected Groups */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    🔒 Private and Protected Groups
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.chat.private_and_protected_groups ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Search in Messages */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    🔍 Search in Messages
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.chat.search_in_messages ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Block Users */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    🚫 Block Users
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.chat.block_users ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Ban User */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    🚫 Ban Users
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.chat.ban_user ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Mute Users */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    🔇 Mute Users
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.chat.mute_users ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Voice Audio Messages */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    🎤 Voice Audio Messages
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.chat.voice_audio_messages ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Pin Messages */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    📌 Pin Messages
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.chat.pin_messages ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Polls in Groups */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    📊 Polls in Groups
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.chat.polls_in_groups ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Additional Audio Call Features Header */}
                <TableRow>
                  <TableCell
                    colSpan={plans.length + 1}
                    sx={{
                      background: 'linear-gradient(135deg, rgba(0, 122, 255, 0.1) 0%, rgba(95, 176, 255, 0.1) 100%)',
                      fontWeight: 'bold',
                      fontSize: '0.95rem',
                      py: 1.5,
                      textAlign: 'center',
                    }}
                  >
                    📞 Additional Audio Call Features
                  </TableCell>
                </TableRow>

                {/* One-to-One Call */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    📞 One-to-One Call
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.audio_call.one_to_one_call ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Group Call */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    👥 Group Call
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.audio_call.group_call ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Invite Users */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    👤 Invite Users
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.audio_call.invite_users ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Share Screen */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    🖥️ Share Screen
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.audio_call.share_screen ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Remove Participants */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    👤 Remove Participants
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.audio_call.remove_participants ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Microphone Control */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    🎙️ Microphone Control
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.audio_call.microphone_control ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Additional Video Call Features Header */}
                <TableRow>
                  <TableCell
                    colSpan={plans.length + 1}
                    sx={{
                      background: 'linear-gradient(135deg, rgba(0, 122, 255, 0.1) 0%, rgba(95, 176, 255, 0.1) 100%)',
                      fontWeight: 'bold',
                      fontSize: '0.95rem',
                      py: 1.5,
                      textAlign: 'center',
                    }}
                  >
                    📹 Additional Video Call Features
                  </TableCell>
                </TableRow>

                {/* One-to-One Call */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    📞 One-to-One Call
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.video_call.one_to_one_call ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Group Call */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    👥 Group Call
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.video_call.group_call ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Invite Users */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    👤 Invite Users
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.video_call.invite_users ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Share Screen */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    🖥️ Share Screen
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.video_call.share_screen ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Remove Participants */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    👤 Remove Participants
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.video_call.remove_participants ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Microphone Control */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    🎙️ Microphone Control
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      {plan.features.video_call.microphone_control ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CloseRoundedIcon color="error" />
                      )}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Storage */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <StorageIcon fontSize="small" color="primary" />
                      Storage
                    </Box>
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      <Typography variant="body2" fontWeight="500">
                        {plan.limits.storage_gb}GB
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>

                {/* Overage Charges Header */}
                <TableRow>
                  <TableCell
                    colSpan={plans.length + 1}
                    sx={{
                      background: 'linear-gradient(135deg, rgba(0, 122, 255, 0.1) 0%, rgba(95, 176, 255, 0.1) 100%)',
                      fontWeight: 'bold',
                      fontSize: '0.95rem',
                      py: 1.5,
                      textAlign: 'center',
                    }}
                  >
                    💳 Additional Charges (Billed Separately Based on Actual Usage)
                  </TableCell>
                </TableRow>

                {/* Audio Overage */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    Audio per minute
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      <Typography variant="body2">
                        {getCurrencySymbol(plan.currency || '')}
                        {plan.overage_charges.audio_per_minute}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>

                {/* Video Overage */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    Video per minute
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      <Typography variant="body2">
                        {getCurrencySymbol(plan.currency || '')}
                        {plan.overage_charges.video_per_minute}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>

                {/* Storage Overage */}
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    Extra storage per GB
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      <Typography variant="body2">
                        {getCurrencySymbol(plan.currency || '')}
                        {plan.overage_charges.extra_storage_per_gb}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.05)' } }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      background: 'white',
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      pl: 4,
                    }}
                  >
                    Extra charges per extra user
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.plan_id} align="center">
                      <Typography variant="body2">
                        {getCurrencySymbol(plan.currency || '')}
                        {plan.overage_charges.per_extra_user}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions
          sx={{
            background: 'linear-gradient(135deg, #e5f3ff 0%, #cce7ff 100%)',
            px: 3,
            py: 2,
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Select a plan that fits your needs
          </Typography>
          <Button
            variant="contained"
            onClick={() => setCompareModalOpen(false)}
            sx={{
              background: 'linear-gradient(135deg, #007aff 0%, #5fb0ff 100%)',
              fontWeight: 'bold',
              px: 3,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Fade in={true} timeout={1200}>
        <Box 
          textAlign="center" 
          mt={8}
          sx={{
            background: 'linear-gradient(135deg, #007aff 0%, #5fb0ff 100%)',
            borderRadius: 4,
            p: 6,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-50%',
              right: '-50%',
              width: '200%',
              height: '200%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
              animation: 'ripple 15s infinite',
            },
            '@keyframes ripple': {
              '0%': {
                transform: 'translate(0, 0)',
              },
              '50%': {
                transform: 'translate(-30%, -30%)',
              },
              '100%': {
                transform: 'translate(0, 0)',
              },
            },
          }}
        >
          <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ position: 'relative', zIndex: 1 }}>
            Need a Custom Solution?
          </Typography>
          <Typography variant="h6" mb={4} sx={{ opacity: 0.9, position: 'relative', zIndex: 1, maxWidth: 600, mx: 'auto' }}>
            Looking for enterprise-grade features, custom integrations, or tailored pricing? 
            Our sales team is ready to help you build the perfect solution.
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            onClick={() => navigate('/support')}
            sx={{
              position: 'relative',
              zIndex: 1,
              background: 'white',
              color: '#007aff',
              fontWeight: 'bold',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'white',
                transform: 'translateY(-4px) scale(1.05)',
                boxShadow: '0 12px 24px rgba(0,0,0,0.3)',
              }
            }}
          >
            Contact Sales Team
          </Button>
        </Box>
      </Fade>
    </Box>
  );
};

export default Plans;
